import clsx from 'clsx'
import {find, findIndex, isEmpty, omit} from 'lodash'
import moment from 'moment'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {CSSTransition} from 'react-transition-group'
import {A1BatchesFilter, AddToA1BatchModal, UploadA1Batch} from '../..'
import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  FORMAT_DATE,
  COMMON_OUTER_HEIGHT,
  ROUTES,
  TABLE_PADDING,
  TABLE_KEY_COLUMN,
  TOP_BUTTONS_HEIGHT,
  APP_FOOTER_HEIGHT,
} from '../../../../../_gori/constants'
import {convertUserTimeZone, KTSVG, StorageHelpers, useDisabled} from '../../../../../_gori/helpers'
import {TableSkeleton} from '../../../../../_gori/helpers/components'
import useCancelToken from '../../../../../_gori/hooks/UseCancelToken'
import {PageTitle} from '../../../../../_gori/layout/core'
import {
  Button,
  DropdownButton,
  Table,
  TableBody,
  TableHeader,
  TableTabs,
  TableWrapper,
  ValidationErrorModal,
} from '../../../../../_gori/partials/widgets'
import {useAuth} from '../../../auth'
import {DragOverlay, useDragOver} from '../../../common'
import A1_CONSTANTS from '../../core/_const'
import A1BatchService from '../../core/_requests'
import {BatchScan} from '../../modals/batches-page/scan/BatchScan'
import {getCSSVariableValue} from '../../../../../_gori/assets/ts/_utils'
import {UseTopAlert} from '../../../../../_gori/hooks/UseTopAlert'
import {RenameA1Batch} from '../batches-detail/RenameA1Batch'
import {RemoveA1Batch} from '../batches-detail/RemoveA1Batch'
import {useA1BatchHelper} from '../../hooks'
import {A1BatchesEmptyState} from './A1BatchesEmptyState'
import {BatchesWalkthrough} from '../../../batches/components/BatchesWalkthrough'

const A1_WALKTHROUGH_STORAGE_KEY = 'a1_batches_walkthrough_dismissed'

const A1BatchesPage: React.FC = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const {loadingSwitch, currentUser} = useAuth()
  const {topAlertHeight} = UseTopAlert()
  const {newCancelToken, isCancel} = useCancelToken()
  const [searchParams, setSearchParams] = useSearchParams()
  const {setPageDisabled} = useDisabled()
  const {downloadA1Batch} = useA1BatchHelper()

  const [show, setShow] = useState<{
    upload: boolean
    hasNotice: boolean
    batchScan: boolean
    addToA1Batch: boolean
  }>({
    upload: false,
    hasNotice: false,
    batchScan: false,
    addToA1Batch: false,
  })
  const [loadingFirst, setLoadingFirst] = useState<boolean>(true)
  const [statistic, setStatistic] = useState<any>([])
  const [tableData, setTableData] = useState<any>([])
  const [pagination, setPagination] = useState<any>({})
  const [highlightBatchIds, setHighlightBatchIds] = useState<string[]>([])
  const [validationErrors, setValidationErrors] = useState<any>()

  const {isDragOver, handleDrop, setIsDragOver} = useDragOver()
  const [hasOpenModal, setHasOpenModal] = useState(false)
  const [showWalkthrough, setShowWalkthrough] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Observe document.body's 'modal-open' class to disable DragOverlay whenever any modal is open.
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setHasOpenModal(document.body.classList.contains('modal-open'))
    })
    observer.observe(document.body, {attributes: true, attributeFilter: ['class']})
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!loadingFirst && !isEmpty(tableData) && currentUser?.settings?.a1_user) {
      const dismissed = StorageHelpers.getItemLocalStorage(A1_WALKTHROUGH_STORAGE_KEY)
      if (!dismissed) {
        setShowWalkthrough(true)
      }
    }
  }, [loadingFirst, tableData, currentUser?.settings?.a1_user])

  const handleDismissWalkthrough = useCallback(() => {
    StorageHelpers.setItemLocalStorage(A1_WALKTHROUGH_STORAGE_KEY, true)
    setShowWalkthrough(false)
  }, [])

  const handleFileSelect = useCallback(
    (file: File) => {
      if (!A1_CONSTANTS.UPLOAD_CONFIG.SUPPORTED_FORMATS.includes(file.type)) {
        toast.error(intl.formatMessage({id: 'INVALID_FILE_TYPE_CSV_ONLY'}))
        return
      }
      setUploadFile(file)
      setShow((prev) => ({...prev, addToA1Batch: true}))
    },
    [intl]
  )

  const handleDragDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      const file = handleDrop(e)
      if (file) {
        handleFileSelect(file)
      }
    },
    [handleDrop, handleFileSelect]
  )

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        handleFileSelect(file)
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [handleFileSelect]
  )

  const baseColor = getCSSVariableValue('--bs-primary')

  const listStatus = Object.values(omit(A1_CONSTANTS.OPTIONS_JOURNEY_STATUS, 'N_A'))

  const initFilterValue = useMemo(
    () => ({
      start: moment().subtract(29, 'days').startOf('day').format(),
      end: moment().endOf('day').format(),
    }),
    []
  )

  const adjustPageAfterDelete = useCallback(() => {
    const currentPage = parseInt(searchParams.get('page') || DEFAULT_PAGE.toString())
    const pageSize = parseInt(searchParams.get('page_size') || DEFAULT_PAGE_SIZE.toString())

    if (pagination.total) {
      const currentRecordsOnPage = tableData.length
      const totalPages = Math.ceil(pagination.total / pageSize)

      if (currentRecordsOnPage === 1 && currentPage > 1 && currentPage === totalPages) {
        const newPage = currentPage - 1
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.set('page', newPage.toString())
        setSearchParams(newSearchParams, {replace: true})
        return true
      }
    }
    return false
  }, [searchParams, setSearchParams, pagination.total, tableData.length])

  const setTable = useCallback(
    async (newHighlightBatchIds?: string[]) => {
      setPageDisabled(true)

      if (newHighlightBatchIds) {
        setHighlightBatchIds(newHighlightBatchIds)
      }

      const config = {
        params: {
          page: searchParams.get('page') || DEFAULT_PAGE,
          page_size: searchParams.get('page_size') || DEFAULT_PAGE_SIZE,
          search_text: searchParams.get('search_text') || null,
          sort_by: searchParams.get('sort_by') || null,
          sort_direction: searchParams.get('sort_direction') || null,
          status: searchParams.get('status') || null,
          // BEGIN: Order filters
          date_from: searchParams.get('date_from') || initFilterValue.start,
          date_to: searchParams.get('date_to') || initFilterValue.end,
          code: searchParams.get('code') || null,
          name: searchParams.get('name') || null,
          readied: searchParams.get('readied') || null,
          origin: searchParams.get('origin') || null,
          destination: searchParams.get('destination') || null,
          journey_status: searchParams.get('journey_status') || null,
          // END: Order filters
        },
        cancelToken: newCancelToken(),
      }
      try {
        const response = await A1BatchService.getList(config)
        setTableData(response.a1_batches)
        setPagination(response.pagination)
        setStatistic(response.statistics)
      } catch (error) {
        if (isCancel(error)) return
      } finally {
        setPageDisabled(false)
        setLoadingFirst(false)
      }
    },
    [
      initFilterValue.end,
      initFilterValue.start,
      isCancel,
      newCancelToken,
      searchParams,
      setPageDisabled,
    ]
  )

  const getListDropDownBtn = useCallback(
    (id: string, name: string, isEditable: boolean) => [
      {
        children: (
          <RenameA1Batch
            id={id}
            name={name}
            disabled={!isEditable}
            onSuccess={() => setTable()}
            onError={(error) => setValidationErrors(error)}
          />
        ),
      },
      {
        children: (
          <RemoveA1Batch
            id={id}
            disabled={!isEditable}
            onSuccess={() => {
              adjustPageAfterDelete()
              setTable()
            }}
            onError={(error) => setValidationErrors(error)}
          />
        ),
      },
      {
        label: intl.formatMessage({id: 'DOWNLOAD_A1_BATCH_LIST'}),
        action: () => {
          downloadA1Batch({batchIds: [id], batchName: name})
        },
        className: 'cursor-pointer',
      },
    ],
    [adjustPageAfterDelete, downloadA1Batch, intl, setTable]
  )

  const columns = useMemo(
    () => [
      {
        id: 'created_at',
        Header: intl.formatMessage({id: 'DATE'}),
        headerClassName: 'min-w-100px ps-4',
        Cell: ({row}: {row: any}) =>
          row.original.created_at && (
            <span className='fw-bold ps-4'>
              {convertUserTimeZone(
                row.original.created_at,
                currentUser,
                process.env.REACT_APP_DATE_FORMAT
              )}
            </span>
          ),
      },
      {
        id: 'code',
        Header: intl.formatMessage({id: 'A1_BATCH_ID'}),
        headerClassName: 'min-w-150px',
        cellClassName: 'fw-bolder fs-6 border-0',
        Cell: ({row}: {row: any}) => (
          <div
            className='fw-bold text-hover-primary cursor-pointer'
            onClick={() =>
              currentUser?.settings?.a1_user && navigate(`${ROUTES.A1.BASE}/${row.original.id}`)
            }
          >
            {row.original.code}
          </div>
        ),
      },
      {
        id: 'name',
        Header: intl.formatMessage({id: 'A1_BATCH_NAME'}),
        headerClassName: 'min-w-150px',
        cellClassName: 'fw-bolder fs-6 border-0',
        Cell: ({row}: {row: any}) => (
          <div className='d-flex gap-2 align-items-center'>
            <div
              onClick={() =>
                currentUser?.settings?.a1_user && navigate(`${ROUTES.A1.BASE}/${row.original.id}`)
              }
              className='text-hover-primary text-dark cursor-pointer'
            >
              {row.original.name}
            </div>
            <div
              className='cursor-pointer'
              onClick={() => {
                currentUser?.settings?.a1_user &&
                  navigate(`${ROUTES.A1.BASE}/${row.original.id}`, {
                    state: {ticketModal: true},
                  })
              }}
            >
              {!!row.original?.ticket_comment_unread && (
                <KTSVG
                  path='/media/gori/a1/unread-message.svg'
                  small={false}
                  style={{color: baseColor}}
                />
              )}
            </div>
          </div>
        ),
      },
      {
        id: 'info',
        Header: intl.formatMessage({id: 'A1_BATCH_INFO'}),
        headerClassName: 'min-w-150px',
        Cell: ({row}: {row: any}) => (
          <span className='d-flex align-items-center'>
            <KTSVG
              path={`/media/gori/a1/${row.original.readied ? 'verify_smooth' : 'incomplete'}.svg`}
              className={clsx('svg-icon-2 text-primary me-2', {
                'text-primary': row.original.readied,
                'text-danger': !row.original.readied,
              })}
            />
            <span className='fs-5 fw-bold'>
              {intl.formatMessage({id: row.original.readied ? 'COMPLETE' : 'INCOMPLETE'})}
            </span>
          </span>
        ),
      },
      {
        id: 'journey_status',
        Header: intl.formatMessage({id: 'STATUS'}),
        headerClassName: 'min-w-100px',
        Cell: ({row}: {row: any}) => {
          const journeyStatus = find(listStatus, {
            value: [row.original.journey_status],
          }) || {...A1_CONSTANTS.OPTIONS_JOURNEY_STATUS.N_A, color: '#F1F4F9'}

          const journeyStatusIndex = findIndex(listStatus, {
            value: [row.original.journey_status],
          })

          return (
            <div>
              <div className='d-flex gap-1'>
                {listStatus.map((status, index) => {
                  return (
                    <div
                      className='w-6px h-6px rounded-circle'
                      style={{
                        backgroundColor:
                          index <= journeyStatusIndex ? journeyStatus?.color : '#F1F4F9',
                      }}
                    />
                  )
                })}
              </div>
              <span className='fs-5 fw-bold mt-1'>
                {intl.formatMessage({id: journeyStatus?.label})}{' '}
              </span>
            </div>
          )
        },
      },
      {
        id: 'pickup_scheduled',
        Header: intl.formatMessage({id: 'PICKUP_SCHEDULED'}),
        headerClassName: 'min-w-100px',
        Cell: ({row}: {row: any}) =>
          (row.original?.actual_pickup_at || row.original?.estimated_pickup_at) && (
            <span className='d-flex align-items-center'>
              <span className='fs-5 fw-bold text-gray-700'>
                {convertUserTimeZone(
                  row.original?.actual_pickup_at || row.original?.estimated_pickup_at,
                  currentUser,
                  process.env.REACT_APP_DATE_FORMAT
                )}
              </span>
            </span>
          ),
      },
      {
        id: 'origin',
        Header: intl.formatMessage({id: 'ORIGIN'}),
        headerClassName: 'min-w-100px',
        Cell: ({row}: {row: any}) => (
          <span className='d-flex align-items-center'>
            <span className='fs-5 fw-bold text-gray-700'>{row.original?.origin?.airport}</span>
          </span>
        ),
      },
      {
        id: 'destination',
        Header: intl.formatMessage({id: 'DESTINATION'}),
        headerClassName: 'min-w-100px',
        Cell: ({row}: {row: any}) => (
          <span className='d-flex align-items-center'>
            <span className='fs-5 fw-bold text-gray-700'>{row.original?.destination?.airport}</span>
          </span>
        ),
      },
      {
        id: 'origin-partner',
        Header: intl.formatMessage({id: 'ORG_PARTNER'}),
        headerClassName: 'min-w-100px',
        Cell: ({row}: {row: any}) => (
          <span className='d-flex align-items-center'>
            <span className='fs-5 fw-bold text-gray-700'>
              {row.original?.partner?.origin?.name}
            </span>
          </span>
        ),
      },
      {
        id: 'destination-partner',
        Header: intl.formatMessage({id: 'DES_PARTNER'}),
        headerClassName: 'min-w-100px',
        Cell: ({row}: {row: any}) => (
          <span className='d-flex align-items-center'>
            <span className='fs-5 fw-bold text-gray-700'>
              {row.original?.partner?.destination?.name}
            </span>
          </span>
        ),
      },
      {
        id: 'shipments',
        Header: intl.formatMessage({id: 'SHIPMENTS'}),
        headerClassName: 'min-w-100px',
        Cell: ({row}: {row: any}) => (
          <span className='d-flex align-items-center'>
            <span className='fs-5 fw-bold text-gray-700'>{row.original?.shipments}</span>
          </span>
        ),
      },
      {
        id: 'etd',
        Header: intl.formatMessage({id: 'ETD'}),
        headerClassName: 'min-w-100px',
        Cell: ({row}: {row: any}) =>
          row.original?.origin?.etd_at && (
            <span className='d-flex align-items-center'>
              <span className='fs-5 fw-bold text-gray-700'>
                {convertUserTimeZone(row.original?.origin?.etd_at, currentUser, FORMAT_DATE.DATE)}
              </span>
            </span>
          ),
      },
      {
        id: 'eta',
        Header: intl.formatMessage({id: 'ETA'}),
        headerClassName: 'min-w-100px',
        Cell: ({row}: {row: any}) =>
          row.original?.destination?.eta_at && (
            <span className='d-flex align-items-center'>
              <span className='fs-5 fw-bold text-gray-700'>
                {convertUserTimeZone(
                  row.original?.destination?.eta_at,
                  currentUser,
                  FORMAT_DATE.DATE
                )}
              </span>
            </span>
          ),
      },
      {
        id: TABLE_KEY_COLUMN.SUB_ACTIONS,
        cellClassName: 'fixed-column',
        Cell: ({row}: {row: any}) => {
          const isEditable = [A1_CONSTANTS.A1_BATCH_STATUS.OPEN].includes(row.original.status)
          const listDropdownBtn = getListDropDownBtn(row.original.id, row.original.name, isEditable)

          return (
            <DropdownButton
              loading={row.original.isLoading}
              list={listDropdownBtn}
              className='d-flex justify-content-center'
            />
          )
        },
      },
    ],
    [intl, currentUser, navigate, baseColor, listStatus, getListDropDownBtn]
  )

  useEffect(() => {
    if (loadingSwitch) return

    setTable()
  }, [loadingSwitch, setTable])

  return (
    <>
      {/*  TODO: [N] handle with highlighted smart A1 batch */}
      <UploadA1Batch
        show={show.upload}
        handleClose={() => {
          setShow((prev) => ({...prev, upload: false}))
          if (isDragOver) {
            setIsDragOver(false)
          }
        }}
        handleSuccess={(highlightBatchIds) => setTable(highlightBatchIds)}
      />
      <input
        ref={fileInputRef}
        type='file'
        accept={A1_CONSTANTS.UPLOAD_CONFIG.SUPPORTED_FORMATS.join(',')}
        onChange={handleFileInputChange}
        style={{display: 'none'}}
      />
      {show.addToA1Batch && (
        <AddToA1BatchModal
          show={show.addToA1Batch}
          handleClose={() => {
            setShow((prev) => ({...prev, addToA1Batch: false}))
            setUploadFile(null)
          }}
          file={uploadFile}
          reloadTable={setTable}
        />
      )}
      <BatchScan
        show={show.batchScan}
        handleClose={() => setShow((prev) => ({...prev, batchScan: false}))}
        handleSuccess={() => setTable()}
      />
      <BatchesWalkthrough isVisible={showWalkthrough} onDismiss={handleDismissWalkthrough} />
      <PageTitle>
        <span className='d-flex align-items-center'>
          {intl.formatMessage({id: 'A1_BATCHES'})}
          <KTSVG
            path={`/media/gori/a1/${show.hasNotice ? 'has_notice' : 'no_notice'}.svg`}
            className='svg-icon-1 ms-2 text-gray-700'
          />
        </span>
      </PageTitle>
      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        {loadingFirst ? (
          <>
            <div className='mb-5'>
              <div className='btn col-6 col-md-2 placeholder placeholder-lg rounded-2 bg-white' />
            </div>
            <TableWrapper>
              <div className='table-content--inset-inner h-100'>
                <TableBody>
                  <div className='d-flex justify-content-between my-5'>
                    <div className='btn col-2 placeholder placeholder-lg rounded-2 bg-secondary' />
                    <div className='col-6 col-md-6 d-flex justify-content-end gap-2'>
                      <div className='btn col-5 col-md-4 placeholder placeholder-lg rounded-2 bg-secondary' />
                      <div className='btn placeholder placeholder-lg rounded-2 bg-secondary mx-2' />
                      <div className='btn col-3 col-md-2 placeholder placeholder-lg rounded-2 bg-secondary' />
                    </div>
                  </div>
                  <TableSkeleton countColumn={4} />
                </TableBody>
              </div>
            </TableWrapper>
          </>
        ) : (
          <div className='position-relative'>
            <TableTabs
              dataTabs={statistic}
              keyCheckActive='status'
              children={
                <TableWrapper className='rounded-top-left-0-dt'>
                  <div className='table-content--inset-inner h-100'>
                    <TableHeader className='min-h-1px my-5 mb-8 d-flex flex-column'>
                      <A1BatchesFilter />
                    </TableHeader>
                    <TableBody
                      className='position-relative'
                      height={`calc(100vh - ${
                        COMMON_OUTER_HEIGHT -
                        TOP_BUTTONS_HEIGHT -
                        TABLE_PADDING * 2 +
                        -APP_FOOTER_HEIGHT +
                        topAlertHeight
                      }px)`}
                    >
                      {currentUser?.settings?.a1_user && !hasOpenModal && (
                        <DragOverlay isVisible={isDragOver} onDrop={handleDragDrop} />
                      )}
                      {isEmpty(tableData) ? (
                        currentUser?.settings?.a1_user ? (
                          <A1BatchesEmptyState onFileSelect={handleFileSelect} />
                        ) : (
                          <div className='d-flex flex-column flex-fill justify-content-center align-items-center py-10'>
                            <button
                              className='btn btn-advanced-primary'
                              onClick={() =>
                                navigate(ROUTES.SETTINGS.PAYMENT_PLAN, {
                                  state: {openChangePlan: true},
                                })
                              }
                            >
                              {intl.formatMessage({id: 'UPGRADE_TO_ENTERPRISE'})}
                            </button>
                            <div className='mt-6 fs-4 fw-bold'>
                              <span className='text-gray-900'>
                                {intl.formatMessage({id: 'CURIOUS_ABOUT_SHIPBAE_A1'})}
                              </span>
                              <a
                                href='/help-center/a1/destination-partner'
                                target='_blank'
                                rel='noreferrer'
                                className='btn btn-link fs-4 fw-bold ms-2 btn-a1-help-center'
                              >
                                {intl.formatMessage({id: 'LEARN_MORE_HERE'})}
                              </a>
                            </div>
                          </div>
                        )
                      ) : (
                        <Table
                          columns={columns}
                          data={tableData}
                          pagination={pagination}
                          usePagination={hasOpenModal || !isDragOver}
                          highlightRowIds={highlightBatchIds}
                        />
                      )}
                    </TableBody>
                  </div>
                </TableWrapper>
              }
              childrenTab={
                <>
                  {currentUser?.settings?.a1_user && (
                    <div className='d-flex flex-column flex-sm-row gap-3 mb-3'>
                      <Button
                        classNameDiv='col-12 col-sm-6 col-md-auto'
                        className='btn btn-advanced-secondary w-100'
                        label={intl.formatMessage({id: 'SCAN_TO_BATCH'})}
                        icon={
                          <KTSVG path='/media/gori/a1/scan.svg' className='svg-icon-3 icon-scan' />
                        }
                        event={() => setShow((prev) => ({...prev, batchScan: true}))}
                      />
                      <Button
                        classNameDiv='col-12 col-sm-6 col-md-auto'
                        className='btn btn-advanced-primary btn-upload-a1 w-100'
                        label={intl.formatMessage({id: 'UPLOAD_A1_BATCH'})}
                        icon={
                          <KTSVG
                            path='/media/gori/a1/logo_upload.svg'
                            className='svg-icon-3 icon-upload'
                          />
                        }
                        event={() => setShow((prev) => ({...prev, upload: true}))}
                      />
                    </div>
                  )}
                </>
              }
            />
          </div>
        )}
      </CSSTransition>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}
    </>
  )
}

export {A1BatchesPage}
